import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { ArrowLeft, MapPin, Tag, Truck, Euro, Gift, HandHeart, MessageCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';
import ImageLightbox from '../components/ImageLightbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarketplaceDetail = () => {
  const { id } = useParams();
  const { isAuthenticated, user, token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const it = language === 'it';

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/marketplace/items/${id}`);
        setItem(res.data);
        document.title = res.data.title + ' - Mercatino Solidale Moneyfor';
      } catch { navigate('/marketplace'); }
      finally { setLoading(false); }
    };
    fetchItem();
    return () => { document.title = 'Moneyfor'; };
  }, [id]);

  const handleChat = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/marketplace/items/${id}/request`, {}, { headers: { Authorization: `Bearer ${token}` } });
      navigate('/chat', { state: { conversationId: res.data.conversation_id } });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="min-h-screen bg-stone-100 dark:bg-stone-950"><Navbar /><div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700" /></div></div>;
  if (!item) return null;

  const isSell = item.type === 'sell';
  const isOffer = item.type === 'offer';
  const isOwner = user?.user_id === item.user_id;
  const shareUrl = `${API_URL}/api/share/marketplace/${id}`;

  return (
    <div className="min-h-screen bg-stone-100 dark:bg-stone-950 overflow-y-auto">
      <Navbar />
      {lightboxIdx >= 0 && <ImageLightbox images={item.images} initialIndex={lightboxIdx} onClose={() => setLightboxIdx(-1)} />}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-16">
        <Link to="/marketplace" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-700 dark:text-stone-400 mb-6 text-sm">
          <ArrowLeft className="h-4 w-4" />{it ? 'Torna al Mercatino' : 'Back to Marketplace'}
        </Link>

        <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-sm border border-stone-200 dark:border-stone-700 overflow-hidden">
          {item.images?.length > 0 && (
            <div className={`grid gap-1 ${item.images.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
              {item.images.map((img, i) => (
                <div key={i} className={`${i === 0 && item.images.length > 1 ? 'md:row-span-2' : ''} overflow-hidden cursor-pointer relative`}
                  onClick={() => setLightboxIdx(i)}>
                  <img src={img} alt={item.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" style={{ minHeight: i === 0 ? '300px' : '150px', maxHeight: i === 0 ? '500px' : '250px' }} />
                  {isSell && item.price && i === 0 && (
                    <Badge className="absolute top-3 left-3 bg-amber-500 text-white text-lg font-bold shadow-lg px-4 py-1.5"><Euro className="h-4 w-4 mr-1" />{item.price.toFixed(2)}</Badge>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Badge className={isSell ? 'bg-amber-100 text-amber-700' : isOffer ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
                {isSell ? (it ? 'In vendita' : 'For sale') : isOffer ? (it ? 'Regalo' : 'Gift') : (it ? 'Cerco' : 'Looking for')}
              </Badge>
              <Badge variant="secondary"><Tag className="h-3 w-3 mr-1" />{item.category}</Badge>
              {item.city && <Badge variant="outline"><MapPin className="h-3 w-3 mr-1" />{item.city}</Badge>}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 dark:text-white mb-4" data-testid="item-detail-title">{item.title}</h1>
            <p className="text-stone-600 dark:text-stone-400 leading-relaxed mb-6 whitespace-pre-wrap">{item.description}</p>

            {item.shipping_description && (
              <div className="flex items-center gap-2 text-sm text-stone-500 mb-6 p-3 bg-stone-50 dark:bg-stone-700 rounded-xl">
                <Truck className="h-4 w-4 shrink-0" /><span>{item.shipping_description}</span>
                <Badge variant="outline" className="ml-auto text-xs">{item.shipping_paid_by === 'donor' ? (it ? 'Spedizione gratuita' : 'Free shipping') : (it ? 'A carico' : 'Paid')}</Badge>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-stone-200 dark:border-stone-700">
              <Link to={`/profile/${item.user_id}`} className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={item.user_picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${item.user_id}`} />
                  <AvatarFallback>{item.user_name?.[0]}</AvatarFallback>
                </Avatar>
                <p className="font-semibold text-stone-900 dark:text-white flex items-center gap-1.5">{item.user_name} {item.is_verified && <CheckCircle className="h-4 w-4 text-blue-500" />}</p>
              </Link>
              <div className="flex items-center gap-3">
                <ShareButtons url={shareUrl} title={item.title} description={item.description} language={language} />
                {isAuthenticated && !isOwner && (
                  <Button onClick={handleChat} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6" data-testid="chat-with-owner">
                    <MessageCircle className="h-4 w-4 mr-2" />{it ? 'Chatta' : 'Chat'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MarketplaceDetail;
